import { useTranslator } from '../components/i18n/useTranslator';
import type { AxiosError } from 'axios';
import type { ErrorLocaleKey } from '../../common/error/errorLocaleKeys';

/**
 * Shape of error objects returned from the API layer.
 *
 * This mirrors the structure used by the server and by client‑side error helpers:
 * - `errorLocaleKey`/`context` are used to look up a translated message
 * - `message` acts as a plain‑text fallback when no translation is available
 * - some errors (for example AppError instances) nest these fields inside `inner`
 */
export type ApiError = {
    message?: string;
    errorLocaleKey?: ErrorLocaleKey;
    context?: Record<string, unknown>;
    code?: string;
    status?: number;
    details?: Record<string, unknown>;
    // Some server errors store extra fields, including errorLocaleKey/context,
    // under an `inner` object to keep the top‑level response stable.
    inner?: {
        errorLocaleKey?: ErrorLocaleKey;
        context?: Record<string, unknown>;
        // Allow any additional properties without type errors
        [key: string]: unknown;
    };
};

/**
 * Narrow translation function interface used by the helpers below.
 *
 * This matches the simplest `useTranslator('errors')` signature so that
 * callers can pass either the real translator or a lightweight test double.
 */
export type TranslationFunction = (key: string, defaultValue: string, context?: Record<string, unknown>) => string;

/**
 * Hook used in client components to turn an `ApiError` into a user‑facing string.
 *
 * Behaviour:
 * - when `errorLocaleKey` is present (top‑level or in `inner`), returns a translated message
 * - when there is only `message`, returns it unchanged
 * - when neither are available, falls back to a generic “unknown error” translation
 *
 * Typical usage:
 *   const translateError = useErrorTranslator();
 *   const message = translateError(apiErrorOrValidationError);
 */
export const useErrorTranslator = () => {
    const translate = useTranslator('errors');

    return (error?: ApiError): string => {
        if (!error) {
            // Use a translation key for unknown errors with English default text
            return translate('server.unknown', 'An unknown error occurred');
        }

        // Default generic server error message via translation key
        const defaultMessage =
            error?.message || translate('server.generic', 'Sorry, something went wrong. Please try again.');

        const localeKey = error?.errorLocaleKey || error.inner?.errorLocaleKey;
        const context = error?.context || error.inner?.context || {};

        // If server provides errorLocaleKey (either top-level or in inner), use it for translation
        if (localeKey) {
            return translate(localeKey as never, defaultMessage, context) as string;
        }

        // Fallback to original message
        return defaultMessage;
    };
};

/**
 * Convenience helper for translating an Axios error response using the `errors` namespace.
 *
 * Expects the server to respond with `{ error: ApiError }` and handles:
 * - missing `response`/`error` by returning either the standard “unknown error” translation
 *   or the provided `fallbackMessage` when supplied
 * - `errorLocaleKey`/`context` (top‑level or `inner`) when present
 * - falling back to `error.message` when no translation key is provided, or to `fallbackMessage`
 *   when there is no message
 *
 * Usage:
 *   const translate = useTranslator('errors');
 *   const message = getTranslatedErrorMessage(axiosError, translate);
 *   const messageWithFallback = getTranslatedErrorMessage(axiosError, translate, 'Custom fallback message');
 */
export const getTranslatedErrorMessage = (
    err: AxiosError<{ error?: ApiError }>,
    translate: TranslationFunction,
    fallbackMessage?: string,
): string => {
    const error = err.response?.data?.error;

    // No error payload – either use custom fallback or generic unknown error
    if (!error) {
        return fallbackMessage ?? translate('server.unknown', 'An unknown error occurred');
    }

    const localeKey = error.errorLocaleKey ?? error.inner?.errorLocaleKey;
    const context = error.context ?? error.inner?.context;
    const defaultForTranslate =
        fallbackMessage ?? error.message ?? translate('server.unknown', 'An unknown error occurred');

    if (localeKey) {
        return translate(localeKey, defaultForTranslate, context ?? {});
    }

    // No locale key – prefer the server message, then the custom fallback, then the generic unknown error.
    return error.message ?? fallbackMessage ?? translate('server.unknown', 'An unknown error occurred');
};
